import axios from 'axios';
import { TileLayer } from '@deck.gl/geo-layers';
import { BitmapLayer } from '@deck.gl/layers';

const TITILER_URL = 'https://titiler.lambda.nazkamapps.com';
const WATER_QUALITY_LAYER_ID = 'pagasitikos-water-quality-layer';
const WATER_QUALITY_LAYER_SOURCES = {
  'turbidity': {
    rescale: [0, 15],
    unit: 'NTU',
    titleMatch: '_t_',
  },
  'chlorophyll-a': {
    rescale: [0, 5.0],
    unit: 'mg/m³',
    titleMatch: '_chl_',
  },
};

// Signed urls returned by the data-layer api are valid for 60 minutes. Refresh them a
// little before they actually expire so an in-flight tile request never gets a stale url.
const SIGNED_URL_TTL_MS = 60 * 60 * 1000;
const SIGNED_URL_REFRESH_MARGIN_MS = 5 * 60 * 1000;

// resourceId -> { url, signedAt }
const signedUrlCache = new Map();

function getLayerNameFromResourceTitle(title = '') {
  return Object.keys(WATER_QUALITY_LAYER_SOURCES).find(
    layerName => title.includes(WATER_QUALITY_LAYER_SOURCES[layerName].titleMatch),
  ) || null;
}

/**
 * Group data-layer resources (as returned by /api/data-layers/resources) into the
 * available dates and resourceId lookups needed by the water quality date picker,
 * keyed by layer name ('turbidity' | 'chlorophyll-a').
 */
function getAvailableDatesByLayer(resources = []) {
  const resourceIdsByDate = {
    'turbidity': {},
    'chlorophyll-a': {},
  };

  resources.forEach((resource) => {
    const layerName = getLayerNameFromResourceTitle(resource?.title);
    const sensingTime = resource?.meta_json?.sensingTime;

    if (!layerName || !sensingTime) {
      return;
    }

    const date = sensingTime.slice(0, 10);
    const existingResourceId = resourceIdsByDate[layerName][date];

    // If multiple resources share a date, prefer the most recently created one.
    if (!existingResourceId || resource.created_at > existingResourceId.created_at) {
      resourceIdsByDate[layerName][date] = { resourceId: resource.identifier, created_at: resource.created_at };
    }
  });

  return Object.keys(resourceIdsByDate).reduce((availableDatesByLayer, layerName) => {
    const resourceIdByDate = Object.fromEntries(
      Object.entries(resourceIdsByDate[layerName]).map(([date, { resourceId }]) => [date, resourceId]),
    );

    availableDatesByLayer[layerName] = {
      dates: Object.keys(resourceIdByDate).sort((a, b) => b.localeCompare(a)),
      resourceIdByDate,
    };

    return availableDatesByLayer;
  }, {});
}

async function getSignedResourceUrl(resourceId) {
  const cached = signedUrlCache.get(resourceId);
  const isCloseToExpiring = !cached
    || (Date.now() - cached.signedAt) >= (SIGNED_URL_TTL_MS - SIGNED_URL_REFRESH_MARGIN_MS);

  if (!isCloseToExpiring) {
    return cached.url;
  }

  const { data } = await axios.get(`/api/data-layers/get-url/${resourceId}`);
  signedUrlCache.set(resourceId, { url: data.url, signedAt: Date.now() });

  return data.url;
}

function createTileLayer(id, tileUrl, onHover) {
  return new TileLayer({
    id: id,
    data: tileUrl,
    pickable: true,
    beforeId: 'z-index-4',
    opacity: 0.5,
    onHover: ({ bitmap, sourceLayer }) => {
      if (bitmap) {
        const { device } = sourceLayer.context;

        const pixelColor = device.readPixelsToArrayWebGL(sourceLayer.props.image, {
          sourceX: bitmap.pixel[0],
          sourceY: bitmap.pixel[1],
          sourceWidth: 1,
          sourceHeight: 1,
        });

        onHover(pixelColor);
      }
    },

    renderSubLayers: (props) => {
      const { boundingBox } = props.tile;

      if (!props.data) {
        return null;
      }

      return new BitmapLayer(props, {
        data: null,
        image: props.data,
        bounds: [boundingBox[0][0], boundingBox[0][1], boundingBox[1][0], boundingBox[1][1]],
        pickable: true,
      });
    },
  });
}

async function getLayerUrl(layerName, resourceId) {
  const { rescale } = WATER_QUALITY_LAYER_SOURCES[layerName];
  const signedUrl = await getSignedResourceUrl(resourceId);

  return `${TITILER_URL}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}@2x?url=${encodeURIComponent(signedUrl)}&colormap_name=viridis&rescale=${rescale.join(',')}`;
}

function getLayerConfig(layerName) {
  return WATER_QUALITY_LAYER_SOURCES[layerName] || null;
}

export {
  createTileLayer,
  getAvailableDatesByLayer,
  getLayerConfig,
  getLayerUrl,
  WATER_QUALITY_LAYER_ID,
};
